/**
 * CampaignProposalCard Component
 *
 * Displays a rich, interactive campaign proposal with:
 * - Expandable ad sets and variations
 * - Embedded user media (images/videos)
 * - All headlines and copy options
 * - Publish button for ready campaigns
 */

// Fonts are loaded via editorShared (imported transitively through UnifiedImageEditor)
import '@/components/media/editorShared'; // ensure fonts are loaded for canvas preview rendering

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api';
import { useToast } from '@/components/ui/Toast';
import { RichMessageText } from './RichMessageText';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Modal } from '@/components/ui/Modal';
import { renderPlatformMockup } from '@/components/ads';
import { MultiAccountPublishModal, OrganicPostModal, SchedulePublishModal } from '@/components/publisher';
import type { OrganicPublishResult, ScheduleResult } from '@/components/publisher';
import { PlatformLogo } from '@/components/icons/PlatformLogos';
import type { PlatformKey } from '@/components/icons/PlatformLogos';
import { ExportModal } from '@/components/campaigns/ExportModal';
import { CampaignData, Platform, CampaignStructure, AdSetOrGroup, AdVariation } from '@/lib/csv-export';
import { Download, Crop } from 'lucide-react';
import { UnifiedImageEditor } from '@/components/media/UnifiedImageEditor';
import { getPlatformOutputDimensions } from '@/components/ads/utils/platformConfig';
import type { MediaAdjustments, PlatformAdjustment, StoredTextLayer } from '@/components/media/mediaAdjustmentTypes';
import type { OverlayHint } from '@/components/media/editorShared';
import { computeCenterCrop } from '@/components/media/mediaAdjustmentTypes';
import { renderFinalImage } from '@/components/media/renderFinalImage';
import { DEFAULT_EFFECT_PARAMS } from '@/components/media/effects/presets';
import type { TextLayer } from '@/components/media/editorShared';
import type { EffectId, EffectParams } from '@/components/media/effects/presets';
import { useAuth } from '@/hooks/useAuth';
import { normalizeFilename, extractMediaTags } from '@/utils/mediaTagParser';
import { formatCurrency } from '@/utils/currency';
import { validateProposal, PLATFORM_LIMITS } from '@/utils/platformLimits';
import type { FieldViolation } from '@/utils/platformLimits';

/** Keywords can be plain strings or objects like {text, match_type} from Google Ads */
const getKeywordText = (keyword: any): string =>
  typeof keyword === 'object' && keyword !== null && keyword.text ? keyword.text : String(keyword);

interface CampaignProposalCardProps {
  proposal: any;
  conversationMedia: Array<{ filename: string; url: string; type: string }>;
  resolvedMedia?: Array<{
    filename: string;
    normalized_filename: string;
    url: string;
    media_type: string;
    placements?: string[];
    overlay_suggestion?: OverlayHint | null;
  }>;
  conversationId?: string;
  onPublish?: () => void;
}

export const CampaignProposalCard: React.FC<CampaignProposalCardProps> = ({
  proposal,
  conversationMedia,
  resolvedMedia,
  conversationId,
  onPublish,
}) => {
  const [expandedAdSets, setExpandedAdSets] = useState<Set<string | number>>(new Set([0])); // First ad set expanded by default
  const [expandedVariations, setExpandedVariations] = useState<Set<string>>(new Set());
  const [expandedTechnicalDetails, setExpandedTechnicalDetails] = useState<Set<string>>(new Set());
  const [showJSON, setShowJSON] = useState(false);
  const [showMultiAccountPublish, setShowMultiAccountPublish] = useState(false);
  const [showOrganicPostModal, setShowOrganicPostModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePublishType, setSchedulePublishType] = useState<'paid_ad' | 'organic_post'>('paid_ad');
  const [showAdDropdown, setShowAdDropdown] = useState(false);
  const [showPostDropdown, setShowPostDropdown] = useState(false);
  const [scheduleInfos, setScheduleInfos] = useState<Array<{
    scheduled_at: string;
    timezone: string;
    publish_type: 'paid_ad' | 'organic_post';
    status: string;
  }>>([]);
  const [showScheduleBanner, setShowScheduleBanner] = useState(false);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!showAdDropdown && !showPostDropdown) return;
    const handler = () => { setShowAdDropdown(false); setShowPostDropdown(false); };
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [showAdDropdown, showPostDropdown]);
  const [organicPostResults, setOrganicPostResults] = useState<Array<{
    platform: string;
    accountName: string;
    status: 'published' | 'failed';
    postUrls: string[];
    error?: string;
  }>>([]);
  const { currentOrganization } = useAuth();
  const { toast } = useToast();

  // ─── Load existing organic post and paid ad assignments on mount ─────────
  useEffect(() => {
    if (!conversationId) return;
    const loadAssignments = async () => {
      try {
        const resp = await apiClient.get(`/api/v1/conversations/${conversationId}/platform-assignments`);
        const all = resp.data.assignments || [];

        const organic = all.filter((a: any) => a.publish_type === 'organic_post');
        if (organic.length > 0) {
          setOrganicPostResults(organic.map((a: any) => ({
            platform: a.platform,
            accountName: a.account_name || 'Unknown',
            status: a.status === 'failed' ? 'failed' : 'published',
            postUrls: a.organic_post_urls || [],
            error: undefined,
          })));
        }

      } catch {
        // Non-critical — just won't show saved organic status
      }
    };
    loadAssignments();
  }, [conversationId]);

  // ─── Load active schedules on mount ──────────────────────────────────────
  const loadScheduleInfos = useCallback(async () => {
    if (!conversationId) return;
    try {
      const resp = await apiClient.get('/api/v1/scheduler/', { params: { conversation_id: conversationId } });
      const items = (resp.data?.schedules || resp.data || []).filter(
        (s: any) => s.status === 'scheduled' || s.status === 'publishing'
      );
      setScheduleInfos(items.map((s: any) => ({
        scheduled_at: s.scheduled_at,
        timezone: s.timezone,
        publish_type: s.publish_type,
        status: s.status,
      })));
    } catch {
      // Non-critical
    }
  }, [conversationId]);

  useEffect(() => {
    loadScheduleInfos();
  }, [loadScheduleInfos]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.conversationId === conversationId) {
        loadScheduleInfos();
      }
    };
    window.addEventListener('campaignScheduled', handler);
    return () => window.removeEventListener('campaignScheduled', handler);
  }, [conversationId, loadScheduleInfos]);

  // ─── Media Adjustments (crop + text overlay per platform) ─────────────────

  /** Stored adjustment params { [filename]: { [platform]: PlatformAdjustment | null } } */
  const [mediaAdjustments, setMediaAdjustments] = useState<MediaAdjustments>({});
  /** Canvas-generated preview data URLs { [platform]: { [filename]: dataUrl } } */
  const [platformPreviews, setPlatformPreviews] = useState<Record<string, Record<string, string>>>({});
  /** Which image+platform combo is currently being edited in the crop modal */
  const [cropModalTarget, setCropModalTarget] = useState<{
    filename: string;
    platform: string;
    originalUrl: string;
  } | null>(null);
  /** True once user confirms they have reviewed all media edits */
  const [mediaReviewed, setMediaReviewed] = useState(false);
  /** Tracks save-in-progress to backend */
  const [isSavingAdjustments, setIsSavingAdjustments] = useState(false);
  /** Cached conversation campaign_config for merging adjustments */
  const conversationCampaignConfigRef = useRef<Record<string, unknown>>({});
  /** Prevents auto-crop from running more than once per proposal */
  const autoCropAppliedRef = useRef(false);

  // Handle different response formats:
  // 1. New format: {proposal, resolved_media, media_lookup}
  // 2. Dual-format: {display, canonical}
  // 3. Legacy: raw proposal data

  // First, check if this is the new wrapper format with resolved_media
  const isWrapperFormat = (proposal && typeof proposal === 'object' && 'proposal' in proposal && 'resolved_media' in proposal);
  const actualProposal = isWrapperFormat ? proposal.proposal : proposal;

  // Then handle dual-format (display/canonical) from the actual proposal
  const displayData = (actualProposal && typeof actualProposal === 'object' && 'display' in actualProposal && 'canonical' in actualProposal)
    ? actualProposal.display
    : actualProposal;

  const canonicalData = (actualProposal && typeof actualProposal === 'object' && 'canonical' in actualProposal)
    ? actualProposal.canonical
    : actualProposal;

  // Display data provides campaigns array ready to render
  const campaigns = displayData?.campaigns || [];
  const isArray = Array.isArray(proposal);

  // Resolve currency: proposal > canonical > org context > USD
  const proposalCurrency = displayData?.currency || canonicalData?.currency || displayData?.budget?.currency || currentOrganization?.currency || 'USD';

  // ─── Platform field validation ────────────────────────────────────────────
  const platformViolations = useMemo<FieldViolation[]>(() => {
    if (!canonicalData) return [];
    return validateProposal(canonicalData);
  }, [canonicalData]);

  // Debug logging
  console.log('[CampaignProposalCard] Rendering:', {
    hasProposal: !!proposal,
    isWrapperFormat,
    hasActualProposal: !!actualProposal,
    hasDisplayData: !!displayData,
    campaignsCount: campaigns.length,
    displayDataKeys: displayData ? Object.keys(displayData) : [],
    firstCampaign: campaigns[0] ? {
      platform: campaigns[0].platform,
      name: campaigns[0].name,
      adSetsCount: campaigns[0].ad_sets?.length || 0
    } : null
  });

  const toggleAdSet = (key: string | number) => {
    setExpandedAdSets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleVariation = (key: string) => {
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleTechnicalDetails = (key: string) => {
    setExpandedTechnicalDetails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Merge resolved media from backend with conversation media for RichMessageText
  // Resolved media takes precedence since it's been validated by backend
  const mergedMedia = React.useMemo(() => {
    console.log('[CampaignProposalCard] Building mergedMedia:', {
      hasResolvedMedia: !!resolvedMedia,
      resolvedMediaCount: resolvedMedia?.length || 0,
      conversationMediaCount: conversationMedia.length
    });

    if (!resolvedMedia || resolvedMedia.length === 0) {
      console.log('[CampaignProposalCard] No resolved media, using conversationMedia');
      return conversationMedia;
    }

    // Create a map of resolved media by filename for quick lookup
    const resolvedMap = new Map(
      resolvedMedia.map(m => [m.filename.toLowerCase(), m])
    );

    // Start with resolved media
    const merged: Array<{ filename: string; url: string; type: string }> = resolvedMedia.map(m => ({
      filename: m.filename,
      url: m.url,
      type: m.media_type
    }));

    console.log('[CampaignProposalCard] Merged media (from resolved):', merged.map(m => ({
      filename: m.filename,
      hasUrl: !!m.url
    })));

    // Add any conversation media not in resolved (for backward compatibility)
    for (const media of conversationMedia) {
      if (!resolvedMap.has(media.filename.toLowerCase())) {
        merged.push(media);
      }
    }

    console.log('[CampaignProposalCard] Final mergedMedia count:', merged.length);
    return merged;
  }, [resolvedMedia, conversationMedia]);

  // Build overlay hint lookup: normalized filename → overlay_suggestion from LLM
  const overlayHintMap = React.useMemo(() => {
    if (!resolvedMedia) return new Map<string, OverlayHint | null>();
    return new Map(
      resolvedMedia
        .filter(m => m.overlay_suggestion)
        .map(m => [m.filename.toLowerCase(), m.overlay_suggestion as OverlayHint])
    );
  }, [resolvedMedia]);

  const getMediaUrl = (filename: string): string | null => {
    const normalized = normalizeFilename(filename);
    const media = mergedMedia.find(m => normalizeFilename(m.filename) === normalized);
    return media?.url || null;
  };

  // ─── Load media_adjustments from conversation on mount ────────────────────

  useEffect(() => {
    if (!conversationId) return;
    apiClient.get(`/api/v1/conversations/${conversationId}`)
      .then(res => {
        const config = res.data?.conversation?.campaign_config;
        if (config) {
          conversationCampaignConfigRef.current = config;
          const adj = config.media_adjustments as MediaAdjustments | undefined;
          if (adj && typeof adj === 'object') setMediaAdjustments(adj);
        }
      })
      .catch(() => { /* silently ignore — adjustments just won't be pre-loaded */ });
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Generate canvas previews whenever adjustments change ────────────────

  useEffect(() => {
    const hasAny = Object.values(mediaAdjustments).some(
      platforms => Object.values(platforms).some(a => a !== null),
    );
    if (!hasAny) return;

    const generate = async () => {
      const next: Record<string, Record<string, string>> = {};

      for (const [filename, platforms] of Object.entries(mediaAdjustments)) {
        for (const [platform, adjustment] of Object.entries(platforms)) {
          if (!adjustment) continue;
          const originalUrl = getMediaUrl(filename);
          if (!originalUrl) continue;

          const previewUrl = await generateCropPreview(originalUrl, adjustment);
          if (previewUrl) {
            if (!next[platform]) next[platform] = {};
            next[platform][filename] = previewUrl;
          }
        }
      }

      if (Object.keys(next).length > 0) {
        setPlatformPreviews(prev => {
          const merged = { ...prev };
          for (const [p, files] of Object.entries(next)) {
            merged[p] = { ...(merged[p] || {}), ...files };
          }
          return merged;
        });
      }
    };

    generate();
  }, [mediaAdjustments]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Canvas preview generation (crop + text with full effects) ─────────────

  function generateCropPreview(
    src: string,
    adjustment: PlatformAdjustment,
    previewWidth = 600,
  ): Promise<string | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = async () => {
        try { await document.fonts.ready; } catch { /* proceed with fallback fonts */ }

        const { crop, textLayers: storedLayers } = adjustment;

        // Compute preview dimensions preserving aspect ratio
        const srcW = crop.w_frac * img.naturalWidth;
        const srcH = crop.h_frac * img.naturalHeight;
        const previewH = Math.round(previewWidth * (srcH / srcW));

        // Convert StoredTextLayers to display-space TextLayers at preview dimensions
        const vpW = previewWidth;
        const vpH = previewH;
        const displayLayers: TextLayer[] = (storedLayers as StoredTextLayer[]).map(sl => ({
          id: sl.id || `preview_${Math.random()}`,
          text: sl.text,
          x: sl.x_frac * vpW,
          y: sl.y_frac * vpH,
          width: sl.w_frac * vpW,
          fontSize: sl.fontSize_vw * vpW,
          fontFamily: sl.fontFamily,
          fill: sl.fill,
          fontStyle: sl.fontStyle,
          align: sl.align as 'left' | 'center' | 'right',
          opacity: sl.opacity,
          rotation: sl.rotation,
          effect: (sl.effect || 'none') as EffectId,
          effectParams: { ...DEFAULT_EFFECT_PARAMS, ...(sl.effectParams || {}) } as EffectParams,
          letterSpacing: (sl.letterSpacing_vw || 0) * vpW,
          lineHeight: sl.lineHeight || 1.3,
          bgShape: sl.bgShape || 'none',
          bgColor: sl.bgColor || '#000000',
          bgOpacity: sl.bgOpacity ?? 0.6,
          bgPadding: (sl.bgPadding_vw || 0.025) * vpW,
        }));

        // Compute panFrac from crop for renderFinalImage
        const targetAR = previewWidth / previewH;
        const imageAR = img.naturalWidth / img.naturalHeight;
        let w_frac: number, h_frac: number;
        if (imageAR > targetAR) { h_frac = 1; w_frac = targetAR / imageAR; }
        else { w_frac = 1; h_frac = imageAR / targetAR; }
        const maxPanX = Math.max(0, 1 - w_frac);
        const maxPanY = Math.max(0, 1 - h_frac);
        const panFrac = {
          x: maxPanX > 0 ? crop.x_frac / maxPanX : 0.5,
          y: maxPanY > 0 ? crop.y_frac / maxPanY : 0.5,
        };

        try {
          const blob = await renderFinalImage({
            sourceImg: img,
            naturalSize: { w: img.naturalWidth, h: img.naturalHeight },
            panFrac,
            targetAR,
            outputWidth: previewWidth,
            outputHeight: previewH,
            textLayers: displayLayers,
            viewportSize: { w: vpW, h: vpH },
          });
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      const sep = src.includes('?') ? '&' : '?';
      img.src = `${src}${sep}t=${Date.now()}`;
    });
  }

  // ─── Extract image filenames referenced by an ad variation ───────────────

  function getAdImageFilenames(ad: any): string[] {
    // Use media_tags if present; otherwise extract from text fields
    // (mirrors getMediaTagsFromAd in platformMappers.ts so the edit button
    // appears whenever the mockup can resolve an image)
    let tags: string[] = ad?.media_tags ?? [];

    // Carousel ads (new schema) store images in cards[].media_tag instead of media_tags
    if (tags.length === 0 && Array.isArray(ad?.cards)) {
      for (const card of ad.cards) {
        if (card?.media_tag) tags.push(card.media_tag);
      }
    }

    if (tags.length === 0) {
      const textFields: string[] = [];
      if (ad?.headlines && Array.isArray(ad.headlines)) textFields.push(...ad.headlines);
      if (ad?.primary_text) {
        if (Array.isArray(ad.primary_text)) textFields.push(...ad.primary_text);
        else textFields.push(ad.primary_text);
      }
      if (ad?.descriptions && Array.isArray(ad.descriptions)) textFields.push(...ad.descriptions);
      if (ad?.description && typeof ad.description === 'string') textFields.push(ad.description);
      if (ad?.caption && typeof ad.caption === 'string') textFields.push(ad.caption);
      for (const text of textFields) {
        for (const mt of extractMediaTags(text)) {
          tags.push(mt.fullMatch);
        }
      }
    }

    return tags
      .map((tag: string) => {
        const m = tag.match(/\[MEDIA:([^|]+)\|/);
        return m ? m[1].trim() : null;
      })
      .filter((fn): fn is string => {
        if (!fn) return false;
        // Use normalizeFilename for matching (removes spaces, lowercases)
        // to stay consistent with extractMediaUrl in platformMappers
        const normalized = normalizeFilename(fn);
        const item = mergedMedia.find(m =>
          normalizeFilename(m.filename) === normalized,
        );
        // Only image files (not video)
        return !!item && !item.type?.toLowerCase().includes('video');
      });
  }

  // ─── Mockup with edit button overlay ─────────────────────────────────────

  const [editMenuOpen, setEditMenuOpen] = useState<string | null>(null); // key: `${platform}-${adIndex}`

  // Close edit dropdown on outside click
  useEffect(() => {
    if (!editMenuOpen) return;
    const handler = () => setEditMenuOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [editMenuOpen]);

  /**
   * Resolves the effective mockup platform for rendering.
   * Maps google_ads + channel_type to specific mockup platforms.
   * For DEMAND_GEN, checks ad media to determine video (YouTube) vs image (Display) mockup.
   * Falls back to inferring type from adGroup.name when channel_type is null (LLM output pattern).
   */
  const getMockupPlatform = (campaign: any, ad?: any, adGroup?: any): string => {
    if (campaign.platform === 'google_ads') {
      // Prefer explicit channel_type; fall back to inferring from adGroup name
      const channelType = campaign.channel_type || (() => {
        const name = (adGroup?.name || '').toLowerCase();
        if (name.includes('shopping')) return 'SHOPPING';
        if (name.includes('demand gen') || name.includes('demand_gen')) return 'DEMAND_GEN';
        // Visual/discovery-style names → Demand Gen (image display)
        if (name.includes('display') || name.includes('discovery') || name.includes('visual')) return 'DEMAND_GEN';
        if (name.includes('search') || name.includes('keyword') || name.includes('high intent')) return 'SEARCH';
        return null;
      })();

      switch (channelType) {
        case 'DISPLAY':
          return 'google_display';
        case 'SHOPPING':
          return 'google_shopping';
        case 'DEMAND_GEN': {
          // Check if the ad uses video or image media
          const mediaTags = ad?.media_tags || [];
          const hasVideo = mediaTags.some((tag: string) => tag.includes('type=video'));
          return hasVideo ? 'youtube' : 'google_display';
        }
        case 'SEARCH':
          return 'google_search';
        default:
          return 'google_search';
      }
    }
    return campaign.platform;
  };

  const renderMockupWithEdit = (platform: string, ad: any, adIndex = 0) => {
    const imageFilenames = getAdImageFilenames(ad);

    // Build media array with preview URLs substituted where available
    const effectiveMedia = mergedMedia.map(m => ({
      ...m,
      url: platformPreviews[platform]?.[m.filename] ?? m.url,
    }));

    const menuKey = `${platform}-${adIndex}`;
    const isMultiImage = imageFilenames.length > 1;
    const editedCount = imageFilenames.filter(fn => mediaAdjustments[fn]?.[platform]).length;

    // A filename has an unapplied overlay hint if the LLM provided one and no text layers have been saved yet
    const hasUnappliedHint = (fn: string) => {
      const hint = overlayHintMap.get(fn.toLowerCase());
      if (!hint) return false;
      const adj = mediaAdjustments[fn]?.[platform];
      return !adj?.textLayers?.length;
    };
    const anyUnappliedHint = imageFilenames.some(hasUnappliedHint);

    const openEditor = (filename: string) => {
      const originalUrl = getMediaUrl(filename) ?? '';
      setCropModalTarget({ filename, platform, originalUrl });
      setEditMenuOpen(null);
    };

    return (
      <div className="relative group/mockup">
        {renderPlatformMockup({ platform, ad, conversationMedia: effectiveMedia })}
        {imageFilenames.length > 0 && (
          <div className="absolute top-2 right-2 z-10 opacity-0 group-hover/mockup:opacity-100 transition-all">
            {isMultiImage ? (
              /* ── Multi-image: dropdown to pick which image to edit ── */
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditMenuOpen(prev => prev === menuKey ? null : menuKey); }}
                  title={anyUnappliedHint ? "AI text overlay suggested — click to edit images" : "Edit images for this platform"}
                  className="flex items-center gap-1.5 px-2.5 py-1.5
                             text-xs font-medium rounded-lg
                             bg-black/60 text-white border border-white/20
                             hover:bg-black/80 transition-all"
                >
                  <Crop size={11} />
                  Edit ({imageFilenames.length})
                  {anyUnappliedHint && (
                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block ml-0.5" title="AI text overlay suggested" />
                  )}
                  {!anyUnappliedHint && editedCount > 0 && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block ml-0.5" title={`${editedCount} edited`} />
                  )}
                </button>
                {editMenuOpen === menuKey && (
                  <div className="absolute top-full right-0 mt-1 w-48 rounded-lg overflow-hidden
                                  bg-navy/95 border border-white/15 shadow-xl backdrop-blur-sm">
                    {imageFilenames.map((fn, idx) => {
                      const isEdited = !!mediaAdjustments[fn]?.[platform];
                      const hintPending = hasUnappliedHint(fn);
                      return (
                        <button
                          key={fn}
                          onClick={() => openEditor(fn)}
                          className="w-full text-left px-3 py-2 text-xs text-white
                                     hover:bg-card/10 transition-colors flex items-center gap-2"
                        >
                          <span className="text-ink-3 font-mono w-4">{idx + 1}.</span>
                          <span className="truncate flex-1">{fn}</span>
                          {hintPending && (
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" title="AI text overlay suggested" />
                          )}
                          {!hintPending && isEdited && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" title="Edited" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* ── Single image: direct edit button ── */
              <button
                onClick={() => openEditor(imageFilenames[0])}
                title={hasUnappliedHint(imageFilenames[0]) ? "AI text overlay suggested — click to review" : "Crop & edit image for this platform"}
                className="flex items-center gap-1.5 px-2.5 py-1.5
                           text-xs font-medium rounded-lg
                           bg-black/60 text-white border border-white/20
                           hover:bg-black/80 transition-all"
              >
                <Crop size={11} />
                Edit
                {hasUnappliedHint(imageFilenames[0]) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block ml-0.5" title="AI text overlay suggested" />
                )}
                {!hasUnappliedHint(imageFilenames[0]) && mediaAdjustments[imageFilenames[0]]?.[platform] && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block ml-0.5" title="Adjusted" />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Persist adjustments to backend ──────────────────────────────────────

  const saveAdjustments = useCallback(async (updated: MediaAdjustments) => {
    if (!conversationId) return;
    setIsSavingAdjustments(true);
    try {
      const mergedConfig = {
        ...conversationCampaignConfigRef.current,
        media_adjustments: updated,
      };
      await apiClient.patch(`/api/v1/conversations/${conversationId}`, {
        campaign_config: mergedConfig,
      });
      conversationCampaignConfigRef.current = mergedConfig;
    } catch (err) {
      console.error('[CampaignProposalCard] Failed to save media adjustments', err);
    } finally {
      setIsSavingAdjustments(false);
    }
  }, [conversationId]);

  // ─── Auto-crop: center-crop images to platform dimensions on first load ───

  useEffect(() => {
    if (autoCropAppliedRef.current || !campaigns.length || !mergedMedia.length) return;
    autoCropAppliedRef.current = true;

    // Collect all (filename, platform) pairs from every ad across all campaigns
    const pairs = new Map<string, Set<string>>(); // filename → Set<platform>
    for (const campaign of campaigns) {
      const platform: string = campaign.platform;
      const adSets = campaign.ad_sets || campaign.ad_groups || [];
      for (const adSet of adSets) {
        for (const ad of (adSet.ad_variations || [])) {
          for (const fn of getAdImageFilenames(ad)) {
            if (!pairs.has(fn)) pairs.set(fn, new Set());
            pairs.get(fn)!.add(platform);
          }
        }
      }
    }

    // Filter out pairs that already have adjustments (user-edited or previously saved)
    const needed: Array<{ filename: string; platform: string; url: string }> = [];
    for (const [filename, platforms] of pairs) {
      for (const platform of platforms) {
        if (mediaAdjustments[filename]?.[platform]) continue;
        const url = getMediaUrl(filename);
        if (url) needed.push({ filename, platform, url });
      }
    }
    if (!needed.length) return;

    // Load each image to get natural dimensions, then compute center-crop
    Promise.all(
      needed.map(
        ({ filename, platform, url }) =>
          new Promise<{ filename: string; platform: string; adj: PlatformAdjustment } | null>(
            resolve => {
              const img = new window.Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const dims = getPlatformOutputDimensions(platform as any);
                const crop = computeCenterCrop(
                  img.naturalWidth,
                  img.naturalHeight,
                  dims.width,
                  dims.height,
                );
                resolve({
                  filename,
                  platform,
                  adj: { crop, textLayers: [], targetWidth: dims.width, targetHeight: dims.height },
                });
              };
              img.onerror = () => resolve(null);
              const sep = url.includes('?') ? '&' : '?';
              img.src = `${url}${sep}t=${Date.now()}`;
            },
          ),
      ),
    ).then(results => {
      const autoCrops: MediaAdjustments = {};
      for (const r of results) {
        if (!r) continue;
        if (!autoCrops[r.filename]) autoCrops[r.filename] = {};
        autoCrops[r.filename][r.platform] = r.adj;
      }
      if (!Object.keys(autoCrops).length) return;

      setMediaAdjustments(prev => {
        const merged = { ...prev };
        for (const [fn, platforms] of Object.entries(autoCrops)) {
          if (!merged[fn]) merged[fn] = {};
          for (const [plat, adj] of Object.entries(platforms)) {
            // Don't overwrite user-edited adjustments
            if (!merged[fn][plat]) merged[fn][plat] = adj;
          }
        }
        saveAdjustments(merged);
        return merged;
      });
    });
  }, [campaigns, mergedMedia, saveAdjustments]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handle apply from crop modal ────────────────────────────────────────

  const handleAdjustApply = useCallback(async (adjustment: PlatformAdjustment) => {
    if (!cropModalTarget) return;

    const { filename, platform } = cropModalTarget;

    // Eagerly generate the preview BEFORE closing the modal so the mockup
    // updates immediately instead of waiting for the async useEffect cycle.
    const originalUrl = getMediaUrl(filename);
    if (originalUrl) {
      const previewUrl = await generateCropPreview(originalUrl, adjustment);
      if (previewUrl) {
        setPlatformPreviews(prev => ({
          ...prev,
          [platform]: { ...(prev[platform] || {}), [filename]: previewUrl },
        }));
      }
    }

    setMediaAdjustments(prev => {
      const updated: MediaAdjustments = {
        ...prev,
        [filename]: {
          ...(prev[filename] || {}),
          [platform]: adjustment,
        },
      };
      saveAdjustments(updated);
      return updated;
    });
    setCropModalTarget(null);
    // Reset reviewed flag since media changed
    setMediaReviewed(false);
  }, [cropModalTarget, saveAdjustments, currentOrganization, conversationId, mediaAdjustments]);

  // ─── Campaign config with adjustments (passed to publish modal) ───────────

  const campaignConfigWithAdjustments = useMemo(() => {
    const base = canonicalData || displayData;
    if (!Object.keys(mediaAdjustments).length) return base;
    return { ...base, media_adjustments: mediaAdjustments };
  }, [canonicalData, displayData, mediaAdjustments]);

  // ─── Determine if any image has adjustments (to show reviewed checkbox) ───

  const hasAnyAdjustments = useMemo(() =>
    Object.values(mediaAdjustments).some(p => Object.values(p).some(a => a !== null)),
  [mediaAdjustments]);

  // Handle both string ("25-45") and object ({min: 25, max: 45}) formats for age_range
  const formatAgeRange = (ageRange: any): string => {
    if (!ageRange) return '';
    if (typeof ageRange === 'string') return ageRange;
    if (typeof ageRange === 'object' && ageRange.min !== undefined && ageRange.max !== undefined) {
      return `${ageRange.min}-${ageRange.max}`;
    }
    return '';
  };

  const transformCanonicalToMetaFormat = (canonicalConfig: any): any => {
    /**
     * Transform canonical campaign proposal format to Meta API format
     *
     * Canonical format (from LLM):
     * {
     *   campaigns: [{platform: "meta", name: "...", platform_specific: {ad_sets: [...]}}],
     *   duration_days: 30,
     *   performance_goals: {...},
     *   ...
     * }
     *
     * Meta API format (expected by publisher):
     * {
     *   campaign: {name: "...", objective: "..."},
     *   ad_sets: [...]
     * }
     */
    if (!canonicalConfig?.campaigns || canonicalConfig.campaigns.length === 0) {
      return canonicalConfig; // Not canonical format, return as-is
    }

    const metaCampaign = canonicalConfig.campaigns[0]; // Get first campaign

    return {
      campaign: {
        name: metaCampaign.name,
        objective: metaCampaign.objective,
        status: metaCampaign.status || 'PAUSED',
        special_ad_categories: metaCampaign.special_ad_categories || []
      },
      ad_sets: metaCampaign.platform_specific?.ad_sets || []
    };
  };

  const handlePublishClick = () => {
    // Open multi-account publish modal
    setShowMultiAccountPublish(true);
  };

  const handlePublishComplete = (results: import('@/components/publisher/MultiAccountPublishModal').PublishCompletedResult[]) => {
    setShowMultiAccountPublish(false);
    // Dispatch an event so ChatInterface can inject a status message into the conversation
    if (conversationId && results.length > 0) {
      window.dispatchEvent(new CustomEvent('campaignPublished', {
        detail: { conversationId, results },
      }));
    }
    if (onPublish) {
      onPublish();
    }
  };

  const handleOrganicPostClick = () => {
    // Open organic post modal
    setShowOrganicPostModal(true);
  };

  const handleOrganicPostComplete = async (results: OrganicPublishResult[]) => {
    setShowOrganicPostModal(false);

    // Update local state with results
    const mapped = results.map((r) => ({
      platform: r.platform,
      accountName: r.accountName,
      status: (r.status === 'published' ? 'published' : 'failed') as 'published' | 'failed',
      postUrls: r.postUrls || [],
      error: r.error,
    }));
    setOrganicPostResults((prev) => [...prev, ...mapped]);

    // Save successful organic posts to conversation-service
    if (conversationId) {
      for (const r of results) {
        if (r.status === 'published') {
          try {
            await apiClient.post(
              `/api/v1/conversations/${conversationId}/platform-assignments`,
              {
                platform: r.platform,
                platform_account_id: r.connectionId || '',
                account_name: r.accountName,
                organic_post_ids: [],
                organic_post_urls: r.postUrls || [],
                status: 'published',
              }
            );
          } catch {
            // Non-critical
          }
        }
      }
    }

    if (onPublish) {
      onPublish();
    }
  };

  const handleScheduleAdClick = () => {
    setSchedulePublishType('paid_ad');
    setShowScheduleModal(true);
    setShowAdDropdown(false);
  };

  const handleSchedulePostClick = () => {
    setSchedulePublishType('organic_post');
    setShowScheduleModal(true);
    setShowPostDropdown(false);
  };

  const handleScheduleComplete = (result: ScheduleResult) => {
    setShowScheduleModal(false);
    loadScheduleInfos();
    if (conversationId) {
      window.dispatchEvent(new CustomEvent('campaignScheduled', {
        detail: { conversationId, result },
      }));
    }
  };

  // Transform proposal data to CampaignData format for CSV export
  const campaignDataForExport = useMemo((): CampaignData => {
    // Get platform list from the display data
    const platforms = (displayData?.platforms || ['meta']).filter(
      (p: string): p is Platform => ['meta', 'google_ads', 'twitter', 'pinterest'].includes(p)
    );

    // Extract first ad's content for default values
    const firstCampaign = campaigns[0];
    const firstAdSet = firstCampaign?.ad_sets?.[0] || firstCampaign?.ad_groups?.[0];
    const firstAd = firstAdSet?.ad_variations?.[0];

    // Extract targeting info
    const targeting = firstAdSet?.targeting || {};
    const ageRange = targeting.age_range;
    const ageMin = typeof ageRange === 'object' ? ageRange.min : (ageRange ? parseInt(ageRange.split('-')[0]) : undefined);
    const ageMax = typeof ageRange === 'object' ? ageRange.max : (ageRange ? parseInt(ageRange.split('-')[1]) : undefined);

    // Format genders
    const genderRaw = targeting.gender?.toLowerCase();
    const genders: ('male' | 'female' | 'all')[] =
      genderRaw === 'all' || !genderRaw ? ['all'] :
      genderRaw === 'male' ? ['male'] :
      genderRaw === 'female' ? ['female'] : ['all'];

    // Extract media from conversation media
    const media = mergedMedia.map((m) => ({
      id: m.filename,
      type: (m.type?.toLowerCase().includes('video') ? 'video' : 'image') as 'image' | 'video',
      public_url: m.url,
      filename: m.filename,
    }));

    // Extract headlines and descriptions
    const headlines = firstAd?.headlines || [];
    const descriptions = firstAd?.descriptions || [];
    const primaryText = Array.isArray(firstAd?.primary_text)
      ? firstAd.primary_text[0]
      : firstAd?.primary_text || '';

    // Get landing page URL
    const linkUrl = firstAd?.link_url || firstAd?.landing_page || displayData?.link_url || '';

    // Get budget info
    const budgetTotal = displayData?.budget_total || 0;
    const durationDays = displayData?.duration_days || 30;
    const dailyBudget = budgetTotal > 0 ? budgetTotal / durationDays : 50;

    // Helper to resolve media URL from media tag
    const resolveMediaFromTag = (tag: string): { id: string; type: 'image' | 'video'; public_url: string; filename: string } | null => {
      const match = tag.match(/\[MEDIA:([^\|]+)\|([^\]]+)\]/);
      if (match) {
        const filename = match[1];
        const mediaItem = mergedMedia.find(m => m.filename === filename);
        if (mediaItem) {
          return {
            id: filename,
            type: (mediaItem.type?.toLowerCase().includes('video') ? 'video' : 'image') as 'image' | 'video',
            public_url: mediaItem.url,
            filename: filename,
          };
        }
      }
      return null;
    };

    // Transform ad variation to export format
    const transformAdVariation = (ad: any): AdVariation => {
      // Resolve media from media_tags
      const adMedia = (ad.media_tags || [])
        .map((tag: string) => resolveMediaFromTag(tag))
        .filter((m: any) => m !== null);

      return {
        name: ad.name,
        format: ad.format,
        headlines: ad.headlines || [],
        primary_text: ad.primary_text,
        descriptions: ad.descriptions || [],
        description: ad.description,
        call_to_action: ad.call_to_action,
        link_url: ad.link_url || ad.landing_page,
        landing_page: ad.landing_page,
        media_tags: ad.media_tags,
        media: adMedia.length > 0 ? adMedia : undefined,
      };
    };

    // Transform ad set/group to export format
    const transformAdSetOrGroup = (adSet: any): AdSetOrGroup => {
      return {
        name: adSet.name,
        budget: adSet.budget,
        targeting: adSet.targeting ? {
          locations: adSet.targeting.locations,
          age_range: adSet.targeting.age_range,
          gender: adSet.targeting.gender,
          interests: adSet.targeting.interests,
          income: adSet.targeting.income,
          education: adSet.targeting.education,
        } : undefined,
        keywords: adSet.keywords,
        ad_variations: (adSet.ad_variations || []).map(transformAdVariation),
      };
    };

    // Transform full campaigns structure for multi-row export
    const campaignsForExport: CampaignStructure[] = campaigns.map((campaign: any): CampaignStructure => {
      const platformId = campaign.platform as Platform;
      return {
        platform: platformId,
        name: campaign.name,
        objective: campaign.objective,
        status: campaign.status,
        // Meta uses ad_sets, others use ad_groups
        ad_sets: platformId === 'meta' && campaign.ad_sets
          ? campaign.ad_sets.map(transformAdSetOrGroup)
          : undefined,
        ad_groups: platformId !== 'meta' && campaign.ad_groups
          ? campaign.ad_groups.map(transformAdSetOrGroup)
          : undefined,
      };
    });

    return {
      title: displayData?.title || 'Campaign',
      objective: displayData?.objective || 'traffic',
      platforms,
      budget: {
        daily: dailyBudget,
        lifetime: budgetTotal || undefined,
        currency: proposalCurrency,
      },
      timeline: {
        start_date: new Date().toISOString().split('T')[0],
        end_date: durationDays
          ? new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          : undefined,
      },
      targeting: {
        locations: targeting.locations?.map((loc: string) => ({ country: loc })) || [{ country: 'US' }],
        age_min: ageMin,
        age_max: ageMax,
        genders,
        interests: targeting.interests || [],
      },
      ad_copy: {
        headline: headlines[0] || displayData?.title || '',
        headlines: headlines.length > 1 ? headlines : undefined,
        body_text: primaryText || descriptions[0] || '',
        description: descriptions[0] || '',
        descriptions: descriptions.length > 1 ? descriptions : undefined,
        cta: firstAd?.call_to_action || 'Learn More',
        link_url: linkUrl,
      },
      media,
      // Include full campaigns structure for multi-row export
      campaigns: campaignsForExport,
      platform_specific: {
        google_ads: {
          keywords: firstAdSet?.keywords || [],
          channel_type: displayData?.channel_type || 'SEARCH',
        },
        pinterest: {
          board_id: '', // User needs to provide this
        },
      },
    };
  }, [displayData, campaigns, mergedMedia]);

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  // Don't render if there are no campaigns to display
  if (campaigns.length === 0) {
    console.log('[CampaignProposalCard] No campaigns to display, skipping render');
    return null;
  }

  return (
    <div className="w-full max-w-4xl p-6 bg-gradient-to-br from-blue-50 to-purple-50 border border-work/30 dark:border-border-dark rounded-xl shadow-lg">
      {/* Header - only show for non-array structure */}
      {!isArray && (
        <div className="mb-6 pb-4 border-b border-work/30 dark:border-border-dark">
          <h3 className="text-2xl font-bold text-ink mb-2">
            📊 {displayData.title}
          </h3>
          <p className="text-sm text-ink-2">
            {displayData.objective}
          </p>
        </div>
      )}

      {/* A/B Testing Info Banner */}
      {!isArray && (
        <div className="mb-4">
          <Alert variant="info" title="✨ Smart A/B Testing Enabled">
            All ad variations (headlines, copy, descriptions) will be published to leverage platform A/B testing.
            The platforms will automatically optimize to show the best-performing variations to your audience.
          </Alert>
        </div>
      )}

      {/* Campaign Overview - only show for non-array structure */}
      {!isArray && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card dark:bg-card-darkAlt p-4 rounded-lg border border-line dark:border-border-dark">
            <p className="text-xs font-medium text-ink-3 mb-1">Total Budget</p>
            <p className="text-2xl font-bold text-primary">
              {displayData.budget_total != null ? formatCurrency(displayData.budget_total, proposalCurrency) : 'N/A'}
            </p>
          </div>
          <div className="bg-card dark:bg-card-darkAlt p-4 rounded-lg border border-line dark:border-border-dark">
            <p className="text-xs font-medium text-ink-3 mb-1">Duration</p>
            <p className="text-2xl font-bold text-primary">
              {displayData.duration_days || 'N/A'} days
            </p>
          </div>
          <div className="bg-card dark:bg-card-darkAlt p-4 rounded-lg border border-line dark:border-border-dark">
            <p className="text-xs font-medium text-ink-3 mb-1">Platforms</p>
            <p className="text-sm font-semibold text-ink">
              {displayData.platforms?.join(', ').toUpperCase() || 'N/A'}
            </p>
            {/* Show Google Ads campaign type if present */}
            {displayData.platforms?.includes('google_ads') && displayData.channel_type && (
              <p className="text-xs text-primary font-medium mt-1">
                {displayData.channel_type}
              </p>
            )}
          </div>
          <div className="bg-card dark:bg-card-darkAlt p-4 rounded-lg border border-line dark:border-border-dark">
            <p className="text-xs font-medium text-ink-3 mb-1">Ad Sets</p>
            <p className="text-2xl font-bold text-primary">
              {displayData.ad_sets?.length || 0}
            </p>
          </div>
        </div>
      )}

      {/* Campaigns (New Structure with Meta & Google Ads) */}
      {campaigns && campaigns.length > 0 && (
        <div className="space-y-6 mb-6">
          {campaigns.map((campaign: any, campaignIdx: number) => (
            <div key={campaignIdx}>
              <h4 className="text-lg font-bold text-ink mb-3">
                {campaign.platform === 'google_ads' ? (campaign.channel_type === 'DISPLAY' ? '🖼️' : campaign.channel_type === 'SHOPPING' ? '🛒' : '🔍') : '🔷'} {campaign.name}
              </h4>

              {/* Meta Ad Sets */}
              {campaign.platform === 'meta' && campaign.ad_sets && campaign.ad_sets.length > 0 && (
                <div className="space-y-4">
                  {campaign.ad_sets.map((adSet: any, adSetIdx: number) => (
                    <div key={adSetIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
                      <button
                        onClick={() => toggleAdSet(adSetIdx)}
                        className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-ink-2 transition-transform ${
                              expandedAdSets.has(adSetIdx) ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <h5 className="text-base font-bold text-ink">
                            {adSet.name}
                          </h5>
                        </div>
                        <Badge variant="blue" size="sm">
                          {adSet.budget != null ? formatCurrency(adSet.budget, proposalCurrency) : 'N/A'}
                        </Badge>
                      </button>

                      {expandedAdSets.has(adSetIdx) && (
                        <div className="p-4 border-t border-line dark:border-border-dark">
                          {adSet.targeting && (
                            <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                              <p className="text-sm font-semibold text-ink-2 mb-3">👥 Targeting</p>
                              <div className="grid grid-cols-2 gap-3 text-sm text-ink-2">
                                {adSet.targeting.age_range && <p><strong>Age:</strong> {formatAgeRange(adSet.targeting.age_range)}</p>}
                                {adSet.targeting.gender && <p><strong>Gender:</strong> {adSet.targeting.gender}</p>}
                                {adSet.targeting.income && <p><strong>Income:</strong> {adSet.targeting.income}</p>}
                                {adSet.targeting.education && <p><strong>Education:</strong> {adSet.targeting.education}</p>}
                              </div>
                              {adSet.targeting.interests && adSet.targeting.interests.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {adSet.targeting.interests.map((interest: string, i: number) => (
                                      <Badge key={i} variant="blue" size="sm">
                                        {interest}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {adSet.targeting.locations && adSet.targeting.locations.length > 0 && (
                                <p className="mt-3 text-sm text-ink-2">
                                  <strong>Locations:</strong> {adSet.targeting.locations.join(', ')}
                                </p>
                              )}
                            </div>
                          )}

                          {adSet.ad_variations && adSet.ad_variations.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-ink-2 mb-3">
                                📝 {adSet.ad_variations.length} Ad Variation{adSet.ad_variations.length > 1 ? 's' : ''}
                              </p>
                              <div className="space-y-3">
                                {adSet.ad_variations.map((ad: any, adIdx: number) => {
                                  const varKey = `meta-${campaignIdx}-${adSetIdx}-${adIdx}`;
                                  const techKey = `meta-tech-${campaignIdx}-${adSetIdx}-${adIdx}`;
                                  const adGroup = adSet;
                                  const isExpanded = expandedVariations.has(varKey);
                                  const isTechExpanded = expandedTechnicalDetails.has(techKey);
                                  const mediaUrls = ad.media_tags?.map((tag: string) => {
                                    const match = tag.match(/\[MEDIA:([^\|]+)\|/);
                                    return match ? getMediaUrl(match[1]) : null;
                                  }).filter(Boolean) || [];

                                  return (
                                    <div key={adIdx} className="rounded-lg border border-line dark:border-border-dark overflow-hidden">
                                      {/* Variation Header (Collapsible) */}
                                      <button
                                        onClick={() => toggleVariation(varKey)}
                                        className="w-full p-4 flex justify-between items-center hover:bg-ground transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <svg
                                            className={`w-5 h-5 text-ink-2 transition-transform ${
                                              isExpanded ? 'rotate-90' : ''
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          <div className="text-left">
                                            <p className="text-sm font-medium text-ink">
                                              Ad #{adIdx + 1}
                                            </p>
                                            {ad.format && (
                                              <p className="text-xs text-ink-3">
                                                {ad.format.replace('_', ' ').toUpperCase()}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        {ad.call_to_action && (
                                          <Badge variant="green" size="sm">
                                            {ad.call_to_action}
                                          </Badge>
                                        )}
                                      </button>

                                      {/* Expanded Content */}
                                      {isExpanded && (
                                        <div className="p-4 border-t border-line dark:border-border-dark space-y-4">
                                          {/* Platform-Specific Mockup */}
                                          {renderMockupWithEdit(getMockupPlatform(campaign, ad, adGroup), ad, adIdx)}

                                          {/* Technical Details (Collapsible) */}
                                          <details open={isTechExpanded} className="mt-4">
                                            <summary
                                              onClick={(e) => {
                                                e.preventDefault();
                                                toggleTechnicalDetails(techKey);
                                              }}
                                              className="cursor-pointer text-sm text-ink-3 hover:text-ink-2 font-medium flex items-center gap-2"
                                            >
                                              <svg
                                                className={`w-4 h-4 transition-transform ${
                                                  isTechExpanded ? 'rotate-90' : ''
                                                }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                              </svg>
                                              View Technical Details
                                            </summary>
                                            {isTechExpanded && (
                                              <div className="mt-3 p-4 bg-ground dark:bg-background-dark rounded-lg space-y-3">
                                                {/* Media Preview */}
                                                {mediaUrls.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-2">Media Files:</p>
                                                    <div className="grid grid-cols-2 gap-2">
                                                      {mediaUrls.slice(0, 4).map((url, i) => (
                                                        <img key={i} src={url} alt={`Media ${i + 1}`} className="w-full h-32 object-cover rounded-lg border border-line-2 dark:border-border-dark" />
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Headlines */}
                                                {ad.headlines && ad.headlines.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-1">
                                                      Headlines ({ad.headlines.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                      {ad.headlines.map((h: string, i: number) => (
                                                        <li key={i}><RichMessageText text={h} uploadedMedia={mergedMedia} /></li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}

                                                {/* Primary Text */}
                                                {ad.primary_text && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-1">
                                                      Primary Text:
                                                    </p>
                                                    <p className="text-xs text-ink-2">
                                                      <RichMessageText text={Array.isArray(ad.primary_text) ? ad.primary_text.join(' | ') : ad.primary_text} uploadedMedia={mergedMedia} />
                                                    </p>
                                                  </div>
                                                )}

                                                {/* Descriptions */}
                                                {ad.descriptions && ad.descriptions.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-1">
                                                      Descriptions ({ad.descriptions.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                      {ad.descriptions.map((d: string, i: number) => (
                                                        <li key={i}><RichMessageText text={d} uploadedMedia={mergedMedia} /></li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}

                                                {/* Description (single) */}
                                                {ad.description && !ad.descriptions && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-1">Description:</p>
                                                    <p className="text-xs text-ink-2">
                                                      <RichMessageText text={ad.description} uploadedMedia={mergedMedia} />
                                                    </p>
                                                  </div>
                                                )}

                                                {/* Media Tags */}
                                                {ad.media_tags && ad.media_tags.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-ink-2 mb-1">
                                                      Media Tags:
                                                    </p>
                                                    <p className="text-xs text-ink-2 font-mono">
                                                      {ad.media_tags.join(', ')}
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </details>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Google Ads Ad Groups */}
              {campaign.platform === 'google_ads' && (
                <>
                  {(!campaign.ad_groups || campaign.ad_groups.length === 0) && (
                    <Alert variant="warning">
                      ⚠️ No ad groups found in this campaign
                    </Alert>
                  )}
                  {campaign.ad_groups && campaign.ad_groups.length > 0 && (
                    <div className="space-y-4">
                      {campaign.ad_groups.map((adGroup: any, adGroupIdx: number) => {
                        const groupKey = `google-${campaignIdx}-${adGroupIdx}`;
                        return (
                      <div key={adGroupIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
                        <button
                          onClick={() => toggleAdSet(groupKey)}
                          className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <svg
                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                expandedAdSets.has(groupKey) ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <h5 className="text-base font-bold text-ink">
                              {adGroup.name}
                            </h5>
                            {adGroup.is_pmax && (
                              <Badge variant="blue" size="sm">PMax</Badge>
                            )}
                          </div>
                        </button>

                        {expandedAdSets.has(groupKey) && (
                          <div className="p-4 border-t border-line dark:border-border-dark">
                            {!adGroup.is_pmax && adGroup.keywords && adGroup.keywords.length > 0 && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">🔑 Keywords</p>
                                <div className="flex flex-wrap gap-2">
                                  {adGroup.keywords.map((keyword: any, i: number) => (
                                    <Badge key={i} variant="blue" size="sm">
                                      {getKeywordText(keyword)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {adGroup.is_pmax && adGroup.targeting && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">👥 Audience Signals</p>
                                <div className="grid grid-cols-2 gap-3 text-sm text-ink-2">
                                  {adGroup.targeting.age_range && <p><strong>Age:</strong> {adGroup.targeting.age_range}</p>}
                                  {adGroup.targeting.gender && <p><strong>Gender:</strong> {adGroup.targeting.gender}</p>}
                                </div>
                                {adGroup.targeting.locations && adGroup.targeting.locations.length > 0 && (
                                  <p className="mt-2 text-sm text-ink-2">
                                    <strong>Locations:</strong> {adGroup.targeting.locations.join(', ')}
                                  </p>
                                )}
                                {adGroup.targeting.interests && adGroup.targeting.interests.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {adGroup.targeting.interests.map((interest: string, i: number) => (
                                        <Badge key={i} variant="blue" size="sm">{interest}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {adGroup.ad_variations && adGroup.ad_variations.length > 0 && (
                              <div>
                                <p className="text-sm font-semibold text-ink-2 mb-3">
                                  📝 {adGroup.ad_variations.length} {adGroup.is_pmax ? 'Asset Group' : `Ad${adGroup.ad_variations.length > 1 ? 's' : ''}`}
                                </p>
                                <div className="space-y-3">
                                  {adGroup.ad_variations.map((ad: any, adIdx: number) => {
                                    const varKey = `google-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const techKey = `google-tech-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const isExpanded = expandedVariations.has(varKey);
                                    const isTechExpanded = expandedTechnicalDetails.has(techKey);

                                    return (
                                      <div key={adIdx} className="rounded-lg border border-line dark:border-border-dark overflow-hidden">
                                        {/* Variation Header (Collapsible) */}
                                        <button
                                          onClick={() => toggleVariation(varKey)}
                                          className="w-full p-4 flex justify-between items-center hover:bg-ground transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <svg
                                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                                isExpanded ? 'rotate-90' : ''
                                              }`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <p className="text-sm font-medium text-ink">
                                              Ad #{adIdx + 1}
                                            </p>
                                          </div>
                                          {ad.call_to_action && (
                                            <Badge variant="green" size="sm">
                                              {ad.call_to_action}
                                            </Badge>
                                          )}
                                        </button>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                          <div className="p-4 border-t border-line dark:border-border-dark space-y-4">
                                            {/* Platform-Specific Mockup */}
                                            {renderMockupWithEdit(getMockupPlatform(campaign, ad, adGroup), ad, adIdx)}

                                            {/* Technical Details (Collapsible) */}
                                            <details open={isTechExpanded} className="mt-4">
                                              <summary
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  toggleTechnicalDetails(techKey);
                                                }}
                                                className="cursor-pointer text-sm text-ink-3 hover:text-ink-2 font-medium flex items-center gap-2"
                                              >
                                                <svg
                                                  className={`w-4 h-4 transition-transform ${
                                                    isTechExpanded ? 'rotate-90' : ''
                                                  }`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                View Technical Details
                                              </summary>
                                              {isTechExpanded && (
                                                <div className="mt-3 p-4 bg-ground dark:bg-background-dark rounded-lg space-y-3">
                                                  {/* Headlines */}
                                                  {ad.headlines && ad.headlines.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Headlines ({ad.headlines.length}):
                                                      </p>
                                                      <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                        {ad.headlines.map((h: string, i: number) => (
                                                          <li key={i}><RichMessageText text={h} uploadedMedia={mergedMedia} /></li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {/* Descriptions */}
                                                  {ad.descriptions && ad.descriptions.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Descriptions ({ad.descriptions.length}):
                                                      </p>
                                                      <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                        {ad.descriptions.map((d: string, i: number) => (
                                                          <li key={i}><RichMessageText text={d} uploadedMedia={mergedMedia} /></li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </details>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* TikTok Ad Groups */}
              {campaign.platform === 'tiktok' && (
                <>
                  {(!campaign.ad_groups || campaign.ad_groups.length === 0) && (
                    <Alert variant="warning">
                      ⚠️ No ad groups found in this campaign
                    </Alert>
                  )}
                  {campaign.ad_groups && campaign.ad_groups.length > 0 && (
                    <div className="space-y-4">
                      {campaign.ad_groups.map((adGroup: any, adGroupIdx: number) => {
                        const groupKey = `tiktok-${campaignIdx}-${adGroupIdx}`;
                        return (
                      <div key={adGroupIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
                        <button
                          onClick={() => toggleAdSet(groupKey)}
                          className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <svg
                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                expandedAdSets.has(groupKey) ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <h5 className="text-base font-bold text-ink">
                              {adGroup.name}
                            </h5>
                          </div>
                          {adGroup.budget && (
                            <Badge variant="blue" size="sm">
                              {formatCurrency(adGroup.budget, proposalCurrency)}
                            </Badge>
                          )}
                        </button>

                        {expandedAdSets.has(groupKey) && (
                          <div className="p-4 border-t border-line dark:border-border-dark">
                            {adGroup.targeting && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">👥 Targeting</p>
                                {adGroup.targeting.age_groups && adGroup.targeting.age_groups.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Age Groups:</strong> {adGroup.targeting.age_groups.join(', ')}</p>
                                )}
                                {adGroup.targeting.locations && adGroup.targeting.locations.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Locations:</strong> {adGroup.targeting.locations.join(', ')}</p>
                                )}
                                {adGroup.targeting.interests && adGroup.targeting.interests.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {adGroup.targeting.interests.map((interest: string, i: number) => (
                                        <Badge key={i} variant="purple" size="sm">
                                          {interest}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {adGroup.ad_variations && adGroup.ad_variations.length > 0 && (
                              <div>
                                <p className="text-sm font-semibold text-ink-2 mb-3">
                                  📝 {adGroup.ad_variations.length} Ad{adGroup.ad_variations.length > 1 ? 's' : ''}
                                </p>
                                <div className="space-y-3">
                                  {adGroup.ad_variations.map((ad: any, adIdx: number) => {
                                    const varKey = `tiktok-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const techKey = `tiktok-tech-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const isExpanded = expandedVariations.has(varKey);
                                    const isTechExpanded = expandedTechnicalDetails.has(techKey);

                                    return (
                                      <div key={adIdx} className="rounded-lg border border-line dark:border-border-dark overflow-hidden">
                                        {/* Variation Header (Collapsible) */}
                                        <button
                                          onClick={() => toggleVariation(varKey)}
                                          className="w-full p-4 flex justify-between items-center hover:bg-ground transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <svg
                                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                                isExpanded ? 'rotate-90' : ''
                                              }`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <p className="text-sm font-medium text-ink">
                                              {ad.name || `Ad #${adIdx + 1}`}
                                            </p>
                                          </div>
                                          {ad.call_to_action && (
                                            <Badge variant="green" size="sm">
                                              {ad.call_to_action}
                                            </Badge>
                                          )}
                                        </button>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                          <div className="p-4 border-t border-line dark:border-border-dark space-y-4">
                                            {/* Platform-Specific Mockup */}
                                            {renderMockupWithEdit(getMockupPlatform(campaign, ad, adGroup), ad, adIdx)}

                                            {/* Technical Details (Collapsible) */}
                                            <details open={isTechExpanded} className="mt-4">
                                              <summary
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  toggleTechnicalDetails(techKey);
                                                }}
                                                className="cursor-pointer text-sm text-ink-3 hover:text-ink-2 font-medium flex items-center gap-2"
                                              >
                                                <svg
                                                  className={`w-4 h-4 transition-transform ${
                                                    isTechExpanded ? 'rotate-90' : ''
                                                  }`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                View Technical Details
                                              </summary>
                                              {isTechExpanded && (
                                                <div className="mt-3 p-4 bg-ground dark:bg-background-dark rounded-lg space-y-3">
                                                  {/* Caption */}
                                                  {ad.caption && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">Caption:</p>
                                                      <p className="text-xs text-ink-2 italic">
                                                        <RichMessageText text={ad.caption} uploadedMedia={mergedMedia} />
                                                      </p>
                                                    </div>
                                                  )}

                                                  {/* Landing Page */}
                                                  {ad.landing_page && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">Landing Page:</p>
                                                      <p className="text-xs text-primary break-words">
                                                        {ad.landing_page}
                                                      </p>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </details>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Pinterest Ad Groups */}
              {campaign.platform === 'pinterest' && (
                <>
                  {(!campaign.ad_groups || campaign.ad_groups.length === 0) && (
                    <Alert variant="warning">
                      ⚠️ No ad groups found in this campaign
                    </Alert>
                  )}
                  {campaign.ad_groups && campaign.ad_groups.length > 0 && (
                    <div className="space-y-4">
                      {campaign.ad_groups.map((adGroup: any, adGroupIdx: number) => {
                        const groupKey = `pinterest-${campaignIdx}-${adGroupIdx}`;
                        return (
                      <div key={adGroupIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
                        <button
                          onClick={() => toggleAdSet(groupKey)}
                          className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <svg
                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                expandedAdSets.has(groupKey) ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <h5 className="text-base font-bold text-ink">
                              {adGroup.name}
                            </h5>
                          </div>
                          {adGroup.budget && (
                            <Badge variant="red" size="sm">
                              {formatCurrency(adGroup.budget, proposalCurrency)}
                            </Badge>
                          )}
                        </button>

                        {expandedAdSets.has(groupKey) && (
                          <div className="p-4 border-t border-line dark:border-border-dark">
                            {adGroup.targeting && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">👥 Targeting</p>
                                {adGroup.targeting.age_groups && adGroup.targeting.age_groups.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Age Groups:</strong> {adGroup.targeting.age_groups.join(', ')}</p>
                                )}
                                {adGroup.targeting.locations && adGroup.targeting.locations.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Locations:</strong> {adGroup.targeting.locations.join(', ')}</p>
                                )}
                                {adGroup.targeting.interests && adGroup.targeting.interests.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {adGroup.targeting.interests.map((interest: string, i: number) => (
                                        <Badge key={i} variant="red" size="sm">
                                          {interest}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {adGroup.ad_variations && adGroup.ad_variations.length > 0 && (
                              <div>
                                <p className="text-sm font-semibold text-ink-2 mb-3">
                                  📝 {adGroup.ad_variations.length} Pin{adGroup.ad_variations.length > 1 ? 's' : ''}
                                </p>
                                <div className="space-y-3">
                                  {adGroup.ad_variations.map((ad: any, adIdx: number) => {
                                    const varKey = `pinterest-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const techKey = `pinterest-tech-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const isExpanded = expandedVariations.has(varKey);
                                    const isTechExpanded = expandedTechnicalDetails.has(techKey);

                                    return (
                                      <div key={adIdx} className="rounded-lg border border-line dark:border-border-dark overflow-hidden">
                                        {/* Variation Header (Collapsible) */}
                                        <button
                                          onClick={() => toggleVariation(varKey)}
                                          className="w-full p-4 flex justify-between items-center hover:bg-ground transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <svg
                                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                                isExpanded ? 'rotate-90' : ''
                                              }`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <p className="text-sm font-medium text-ink">
                                              Pin #{adIdx + 1}
                                            </p>
                                          </div>
                                          {ad.call_to_action && (
                                            <Badge variant="green" size="sm">
                                              {ad.call_to_action}
                                            </Badge>
                                          )}
                                        </button>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                          <div className="p-4 border-t border-line dark:border-border-dark space-y-4">
                                            {/* Platform-Specific Mockup */}
                                            {renderMockupWithEdit(getMockupPlatform(campaign, ad, adGroup), ad, adIdx)}

                                            {/* Technical Details (Collapsible) */}
                                            <details open={isTechExpanded} className="mt-4">
                                              <summary
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  toggleTechnicalDetails(techKey);
                                                }}
                                                className="cursor-pointer text-sm text-ink-3 hover:text-ink-2 font-medium flex items-center gap-2"
                                              >
                                                <svg
                                                  className={`w-4 h-4 transition-transform ${
                                                    isTechExpanded ? 'rotate-90' : ''
                                                  }`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                View Technical Details
                                              </summary>
                                              {isTechExpanded && (
                                                <div className="mt-3 p-4 bg-ground dark:bg-background-dark rounded-lg space-y-3">
                                                  {/* Headlines (Pin Title) */}
                                                  {ad.headlines && ad.headlines.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Pin Title ({ad.headlines.length}):
                                                      </p>
                                                      <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                        {ad.headlines.map((h: string, i: number) => (
                                                          <li key={i}><RichMessageText text={h} uploadedMedia={mergedMedia} /></li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {/* Description */}
                                                  {ad.descriptions && ad.descriptions.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Descriptions ({ad.descriptions.length}):
                                                      </p>
                                                      <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                        {ad.descriptions.map((d: string, i: number) => (
                                                          <li key={i}><RichMessageText text={d} uploadedMedia={mergedMedia} /></li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {ad.description && !ad.descriptions && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">Description:</p>
                                                      <p className="text-xs text-ink-2">
                                                        <RichMessageText text={ad.description} uploadedMedia={mergedMedia} />
                                                      </p>
                                                    </div>
                                                  )}

                                                  {/* Landing Page */}
                                                  {ad.landing_page && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">Landing Page:</p>
                                                      <p className="text-xs text-primary break-words">
                                                        {ad.landing_page}
                                                      </p>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </details>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Twitter/X Ad Groups */}
              {(campaign.platform === 'twitter' || campaign.platform === 'x') && (
                <>
                  {(!campaign.ad_groups || campaign.ad_groups.length === 0) && (
                    <Alert variant="warning">
                      ⚠️ No ad groups found in this campaign
                    </Alert>
                  )}
                  {campaign.ad_groups && campaign.ad_groups.length > 0 && (
                    <div className="space-y-4">
                      {campaign.ad_groups.map((adGroup: any, adGroupIdx: number) => {
                        const groupKey = `twitter-${campaignIdx}-${adGroupIdx}`;
                        return (
                      <div key={adGroupIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
                        <button
                          onClick={() => toggleAdSet(groupKey)}
                          className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <svg
                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                expandedAdSets.has(groupKey) ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <h5 className="text-base font-bold text-ink">
                              {adGroup.name}
                            </h5>
                          </div>
                          {adGroup.budget && (
                            <Badge variant="blue" size="sm">
                              {formatCurrency(adGroup.budget, proposalCurrency)}
                            </Badge>
                          )}
                        </button>

                        {expandedAdSets.has(groupKey) && (
                          <div className="p-4 border-t border-line dark:border-border-dark">
                            {adGroup.keywords && adGroup.keywords.length > 0 && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">🔍 Keywords / Topics</p>
                                <div className="flex flex-wrap gap-2">
                                  {adGroup.keywords.map((keyword: any, i: number) => (
                                    <Badge key={i} variant="blue" size="sm">
                                      {getKeywordText(keyword)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {adGroup.targeting && (
                              <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                                <p className="text-sm font-semibold text-ink-2 mb-3">👥 Targeting</p>
                                {adGroup.targeting.age_groups && adGroup.targeting.age_groups.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Age Groups:</strong> {adGroup.targeting.age_groups.join(', ')}</p>
                                )}
                                {adGroup.targeting.locations && adGroup.targeting.locations.length > 0 && (
                                  <p className="text-sm text-ink-2 mb-2"><strong>Locations:</strong> {adGroup.targeting.locations.join(', ')}</p>
                                )}
                                {adGroup.targeting.interests && adGroup.targeting.interests.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {adGroup.targeting.interests.map((interest: string, i: number) => (
                                        <Badge key={i} variant="blue" size="sm">
                                          {interest}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {adGroup.ad_variations && adGroup.ad_variations.length > 0 && (
                              <div>
                                <p className="text-sm font-semibold text-ink-2 mb-3">
                                  📝 {adGroup.ad_variations.length} Tweet{adGroup.ad_variations.length > 1 ? 's' : ''}
                                </p>
                                <div className="space-y-3">
                                  {adGroup.ad_variations.map((ad: any, adIdx: number) => {
                                    const varKey = `twitter-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const techKey = `twitter-tech-${campaignIdx}-${adGroupIdx}-${adIdx}`;
                                    const isExpanded = expandedVariations.has(varKey);
                                    const isTechExpanded = expandedTechnicalDetails.has(techKey);

                                    return (
                                      <div key={adIdx} className="rounded-lg border border-line dark:border-border-dark overflow-hidden">
                                        {/* Variation Header (Collapsible) */}
                                        <button
                                          onClick={() => toggleVariation(varKey)}
                                          className="w-full p-4 flex justify-between items-center hover:bg-ground transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <svg
                                              className={`w-5 h-5 text-ink-2 transition-transform ${
                                                isExpanded ? 'rotate-90' : ''
                                              }`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <p className="text-sm font-medium text-ink">
                                              Tweet #{adIdx + 1}
                                            </p>
                                          </div>
                                          {ad.call_to_action && (
                                            <Badge variant="blue" size="sm">
                                              {ad.call_to_action}
                                            </Badge>
                                          )}
                                        </button>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                          <div className="p-4 border-t border-line dark:border-border-dark space-y-4">
                                            {/* Platform-Specific Mockup */}
                                            {renderMockupWithEdit(getMockupPlatform(campaign, ad, adGroup), ad, adIdx)}

                                            {/* Technical Details (Collapsible) */}
                                            <details open={isTechExpanded} className="mt-4">
                                              <summary
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  toggleTechnicalDetails(techKey);
                                                }}
                                                className="cursor-pointer text-sm text-ink-3 hover:text-ink-2 font-medium flex items-center gap-2"
                                              >
                                                <svg
                                                  className={`w-4 h-4 transition-transform ${
                                                    isTechExpanded ? 'rotate-90' : ''
                                                  }`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                View Technical Details
                                              </summary>
                                              {isTechExpanded && (
                                                <div className="mt-3 p-4 bg-ground dark:bg-background-dark rounded-lg space-y-3">
                                                  {/* Tweet Text / Headlines */}
                                                  {ad.headlines && ad.headlines.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Tweet Text Options ({ad.headlines.length}):
                                                      </p>
                                                      <ul className="list-disc list-inside text-xs text-ink-2 space-y-0.5">
                                                        {ad.headlines.map((h: string, i: number) => (
                                                          <li key={i}><RichMessageText text={h} uploadedMedia={mergedMedia} /></li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {ad.primary_text && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">
                                                        Primary Text:
                                                      </p>
                                                      <p className="text-xs text-ink-2">
                                                        <RichMessageText text={Array.isArray(ad.primary_text) ? ad.primary_text.join(' | ') : ad.primary_text} uploadedMedia={mergedMedia} />
                                                      </p>
                                                    </div>
                                                  )}

                                                  {/* URL */}
                                                  {ad.link_url && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-ink-2 mb-1">Link:</p>
                                                      <p className="text-xs text-primary break-words">
                                                        {ad.link_url}
                                                      </p>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </details>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legacy Ad Sets (Backward Compatibility) */}
      {!proposal.campaigns && proposal.ad_sets && proposal.ad_sets.length > 0 && (
        <div className="space-y-4 mb-6">
          <h4 className="text-lg font-bold text-ink mb-3">🎯 Ad Sets</h4>
          {proposal.ad_sets.map((adSet: any, adSetIdx: number) => (
            <div key={adSetIdx} className="bg-card dark:bg-card-darkAlt rounded-lg border border-line dark:border-border-dark overflow-hidden">
              {/* Ad Set Header - Clickable */}
              <button
                onClick={() => toggleAdSet(adSetIdx)}
                className="w-full flex justify-between items-center p-4 hover:bg-ground transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-5 h-5 text-ink-2 transition-transform ${
                      expandedAdSets.has(adSetIdx) ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h5 className="text-base font-bold text-ink">
                    {adSet.name}
                  </h5>
                </div>
                <Badge variant="blue" size="sm">
                  {adSet.budget != null ? formatCurrency(adSet.budget, proposalCurrency) : 'N/A'}
                </Badge>
              </button>

              {/* Ad Set Content - Expandable */}
              {expandedAdSets.has(adSetIdx) && (
                <div className="p-4 border-t border-line dark:border-border-dark">
                  {/* Targeting Info */}
                  {adSet.targeting && (
                    <div className="mb-4 p-4 bg-ground dark:bg-background-dark rounded-lg">
                      <p className="text-sm font-semibold text-ink-2 mb-3">👥 Targeting</p>
                      <div className="grid grid-cols-2 gap-3 text-sm text-ink-2">
                        {adSet.targeting.age_range && (
                          <p><strong>Age:</strong> {formatAgeRange(adSet.targeting.age_range)}</p>
                        )}
                        {adSet.targeting.gender && (
                          <p><strong>Gender:</strong> {adSet.targeting.gender}</p>
                        )}
                        {adSet.targeting.income && (
                          <p><strong>Income:</strong> {adSet.targeting.income}</p>
                        )}
                        {adSet.targeting.education && (
                          <p><strong>Education:</strong> {adSet.targeting.education}</p>
                        )}
                      </div>
                      {adSet.targeting.interests && adSet.targeting.interests.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-ink-2 mb-2">Interests:</p>
                          <div className="flex flex-wrap gap-2">
                            {adSet.targeting.interests.map((interest: string, i: number) => (
                              <Badge key={i} variant="blue" size="sm">
                                {interest}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {adSet.targeting.locations && adSet.targeting.locations.length > 0 && (
                        <p className="mt-3 text-sm text-ink-2">
                          <strong>Locations:</strong> {adSet.targeting.locations.join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Ad Variations */}
                  {adSet.ad_variations && adSet.ad_variations.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-ink-2 mb-3">
                        📝 {adSet.ad_variations.length} Ad Variation{adSet.ad_variations.length > 1 ? 's' : ''}
                      </p>
                      <div className="space-y-3">
                        {adSet.ad_variations.map((ad: any, adIdx: number) => {
                          const varKey = `${adSetIdx}-${adIdx}`;
                          const isExpanded = expandedVariations.has(varKey);

                          // Extract media URLs from media_tags
                          const mediaUrls = ad.media_tags?.map((tag: string) => {
                            const match = tag.match(/\[MEDIA:([^\|]+)\|/);
                            if (match) {
                              const filename = match[1];
                              return getMediaUrl(filename);
                            }
                            return null;
                          }).filter(Boolean) || [];

                          return (
                            <div key={adIdx} className="p-4 bg-ground dark:bg-background-dark rounded-lg border border-line dark:border-border-dark">
                              {/* Variation Header */}
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-medium text-ink-3 uppercase">
                                  {ad.format?.replace('_', ' ') || 'Ad'} #{adIdx + 1}
                                </p>
                                {ad.call_to_action && (
                                  <Badge variant="green" size="sm">
                                    {ad.call_to_action}
                                  </Badge>
                                )}
                              </div>

                              {/* Media Preview */}
                              {mediaUrls.length > 0 && (
                                <div className="mb-3 grid grid-cols-2 gap-2">
                                  {mediaUrls.slice(0, 4).map((url, i) => (
                                    <img
                                      key={i}
                                      src={url}
                                      alt={`Media ${i + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border border-line-2 dark:border-border-dark"
                                    />
                                  ))}
                                </div>
                              )}

                              {/* Headlines */}
                              {ad.headlines && ad.headlines.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-sm font-semibold text-ink mb-2">
                                    Headlines ({ad.headlines.length}):
                                  </p>
                                  <div className="space-y-1">
                                    {(isExpanded ? ad.headlines : ad.headlines.slice(0, 2)).map((headline: string, i: number) => (
                                      <p key={i} className="text-sm text-ink-2 pl-4 border-l-2 border-primary/30">
                                        {headline}
                                      </p>
                                    ))}
                                    {ad.headlines.length > 2 && (
                                      <button
                                        onClick={() => toggleVariation(varKey)}
                                        className="text-xs text-primary hover:underline font-medium"
                                      >
                                        {isExpanded
                                          ? 'Show less'
                                          : `+${ad.headlines.length - 2} more headlines`}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Primary Text */}
                              {ad.primary_text && (
                                <div className="mb-3">
                                  <p className="text-xs font-semibold text-ink-2 mb-1">Ad Copy:</p>
                                  <p className="text-sm text-ink-2">
                                    {ad.primary_text}
                                  </p>
                                </div>
                              )}

                              {/* Description */}
                              {ad.description && (
                                <p className="text-xs text-ink-3 italic">
                                  {ad.description}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Performance Goals */}
      {proposal.performance_goals && (
        <div className="bg-card dark:bg-card-darkAlt p-4 rounded-lg border border-line dark:border-border-dark mb-6">
          <h4 className="text-sm font-bold text-ink mb-3">🎯 Performance Goals</h4>
          <div className="grid grid-cols-2 gap-3 text-xs text-ink-2">
            {proposal.performance_goals.total_reach && (
              <p><strong>Reach:</strong> {proposal.performance_goals.total_reach}</p>
            )}
            {proposal.performance_goals.target_ctr && (
              <p><strong>CTR:</strong> {proposal.performance_goals.target_ctr}</p>
            )}
            {proposal.performance_goals.target_roas && (
              <p><strong>ROAS:</strong> {proposal.performance_goals.target_roas}</p>
            )}
            {proposal.performance_goals.estimated_conversions && (
              <p><strong>Est. Conversions:</strong> {proposal.performance_goals.estimated_conversions}</p>
            )}
          </div>
        </div>
      )}

      {/* Review Message */}
      <div className="bg-work-soft border border-work/30 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-900">
          📋 <strong>Review Complete</strong> - Please review the campaign details above. If you'd like any changes,
          just let me know! Otherwise, you can publish the campaign to make it live on your selected platforms.
        </p>
      </div>

      {/* Media review confirmation — only shown when there are pending image adjustments */}
      {hasAnyAdjustments && (
        <label className="flex items-start gap-3 px-4 py-3 mb-3 rounded-lg border
                          border-warn/30 bg-warn-soft
                          cursor-pointer select-none">
          <input
            type="checkbox"
            checked={mediaReviewed}
            onChange={e => setMediaReviewed(e.target.checked)}
            className="mt-0.5 accent-primary shrink-0"
          />
          <span className="text-sm text-amber-900">
            <strong>I have reviewed all image edits</strong> — crop and text overlays will be applied
            when publishing as ads or sharing as posts. The previews shown above are
            approximations; the actual output is rendered by the server at full resolution.
          </span>
        </label>
      )}
      {isSavingAdjustments && (
        <p className="text-xs text-ink-3 mb-2 text-center animate-pulse">Saving image edits…</p>
      )}

      {/* Platform field validation warnings */}
      {platformViolations.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-warn/40 bg-warn-soft">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-warn mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-warn">
                {platformViolations.length} platform field {platformViolations.length === 1 ? 'issue' : 'issues'} detected
              </p>
              <ul className="mt-1.5 space-y-1">
                {platformViolations.map((v, i) => (
                  <li key={i} className="text-xs text-warn flex items-baseline gap-1.5">
                    <span className="inline-block px-1.5 py-0.5 bg-warn-soft rounded text-warn font-medium uppercase tracking-wide">
                      {v.platform}
                    </span>
                    {v.type === 'length' ? (
                      <>
                        <span className="font-medium">{v.label}</span>
                        {' '}is {typeof v.value === 'string' ? v.value.length : v.value} chars
                        {' '}(max {v.limit})
                      </>
                    ) : (
                      <>
                        <span className="font-medium">{v.label}</span>
                        {' '}has {v.value} items (max {v.limit})
                      </>
                    )}
                    {' — '}
                    <span className="text-warn font-mono text-[10px]">{v.path}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-warn">
                These will be truncated by the platform on publish. Ask the AI to revise them if needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Publishes Banner */}
      {scheduleInfos.length > 0 && (
        <div className="rounded-lg border border-work/30 bg-work-soft px-4 py-3 mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-work flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-primary">
                {scheduleInfos.length === 1 ? '1 publish scheduled' : `${scheduleInfos.length} publishes scheduled`}
              </span>
            </div>
            <button
              onClick={() => setShowScheduleBanner(v => !v)}
              className="text-xs text-primary hover:underline"
            >
              {showScheduleBanner ? 'Hide' : 'View details'}
            </button>
          </div>
          {showScheduleBanner && (
            <div className="mt-2 space-y-1.5">
              {scheduleInfos.map((info, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-primary">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${info.status === 'publishing' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                  <span>
                    {new Date(info.scheduled_at).toLocaleString('en-US', {
                      timeZone: info.timezone,
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    {' — '}
                    <span className="capitalize">{info.publish_type.replace('_', ' ')}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={handleExportClick}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-line dark:border-border-dark bg-card dark:bg-card-dark text-sm font-medium text-ink-2 hover:bg-ground transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>

        {/* Share as Posts — split button with schedule dropdown */}
        <div className="relative">
          <div className="flex rounded-lg overflow-hidden border border-work/30/60">
            <button
              onClick={handleOrganicPostClick}
              disabled={!conversationId || (hasAnyAdjustments && !mediaReviewed)}
              title={hasAnyAdjustments && !mediaReviewed ? 'Please confirm you have reviewed all image edits above' : undefined}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-blue-50 to-purple-50 text-sm font-semibold text-primary hover:from-blue-100 hover:to-purple-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </button>
            <button
              onClick={() => { setShowPostDropdown(!showPostDropdown); setShowAdDropdown(false); }}
              disabled={!conversationId || (hasAnyAdjustments && !mediaReviewed)}
              className="px-2 py-2.5 bg-gradient-to-r from-blue-50 to-purple-50 text-primary hover:from-blue-100 hover:to-purple-100 border-l border-work/30/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          {showPostDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-card dark:bg-card-dark rounded-lg shadow-lg border border-line dark:border-border-dark overflow-hidden">
              <button
                onClick={() => { handleOrganicPostClick(); setShowPostDropdown(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-ink-2 hover:bg-ground flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Share Now
              </button>
              <button
                onClick={handleSchedulePostClick}
                className="w-full px-4 py-2.5 text-left text-sm text-ink-2 hover:bg-ground flex items-center gap-2 border-t border-line dark:border-border-dark"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule for Later
              </button>
            </div>
          )}
        </div>

        {/* Publish as Ads — split button with schedule dropdown */}
        <div className="relative">
          <div className="flex rounded-lg overflow-hidden shadow-sm">
            <button
              onClick={handlePublishClick}
              disabled={!conversationId || (hasAnyAdjustments && !mediaReviewed)}
              title={hasAnyAdjustments && !mediaReviewed ? 'Please confirm you have reviewed all image edits above' : undefined}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-sm font-semibold text-white hover:from-primary-dark hover:to-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Publish
            </button>
            <button
              onClick={() => { setShowAdDropdown(!showAdDropdown); setShowPostDropdown(false); }}
              disabled={!conversationId || (hasAnyAdjustments && !mediaReviewed)}
              className="px-2 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-white hover:from-primary-dark hover:to-primary-dark border-l border-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          {showAdDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-card dark:bg-card-dark rounded-lg shadow-lg border border-line dark:border-border-dark overflow-hidden">
              <button
                onClick={() => { handlePublishClick(); setShowAdDropdown(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-ink-2 hover:bg-ground flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Publish Now
              </button>
              <button
                onClick={handleScheduleAdClick}
                className="w-full px-4 py-2.5 text-left text-sm text-ink-2 hover:bg-ground flex items-center gap-2 border-t border-line dark:border-border-dark"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule for Later
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Organic Post Status */}
      {organicPostResults.length > 0 && (
        <div className="mt-3 rounded-lg border border-line dark:border-border-dark bg-ground overflow-hidden">
          <div className="px-4 py-2.5 border-b border-line dark:border-border-dark flex items-center gap-2">
            <svg className="w-4 h-4 text-work" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="text-sm font-medium text-ink-2">
              Shared as Posts
            </span>
            <span className="text-xs text-ink-3">
              {organicPostResults.filter((r) => r.status === 'published').length} published
            </span>
          </div>
          <div className="divide-y divide-line dark:divide-border-dark">
            {organicPostResults.map((result, idx) => (
              <div key={idx} className="px-4 py-2.5 flex items-center gap-3">
                <PlatformLogo
                  platform={(result.platform === 'facebook' ? 'meta' : result.platform) as PlatformKey}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink truncate">
                    {result.accountName}
                  </div>
                  {result.status === 'published' && result.postUrls.length > 0 && (
                    <a
                      href={result.postUrls[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      View post &#8599;
                    </a>
                  )}
                  {result.status === 'failed' && result.error && (
                    <p className="text-xs text-bad truncate">{result.error}</p>
                  )}
                </div>
                {result.status === 'published' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ok-soft text-ok">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-bad-soft text-bad">
                    <span className="w-1.5 h-1.5 rounded-full bg-bad"></span>
                    Failed
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technical Details Toggle */}
      <button
        onClick={() => setShowJSON(!showJSON)}
        className="text-xs text-ink-3 hover:text-ink-2 transition-colors"
      >
        {showJSON ? 'Hide' : 'View'} Technical Details (JSON)
      </button>

      {/* Technical Details (Canonical JSON) */}
      {showJSON && (
        <div className="mt-4 p-4 bg-ground dark:bg-background-dark rounded-lg border border-line dark:border-border-dark">
          <p className="text-xs font-semibold text-ink-2 mb-2">Canonical Format (for Publishing):</p>
          <pre className="text-xs text-ink overflow-x-auto max-h-96">
            {JSON.stringify(canonicalData, null, 2)}
          </pre>
        </div>
      )}

      {/* Multi-Account Publishing Modal */}
      {conversationId && (
        <MultiAccountPublishModal
          isOpen={showMultiAccountPublish}
          onClose={() => setShowMultiAccountPublish(false)}
          conversationId={conversationId}
          campaignConfig={campaignConfigWithAdjustments}
          platforms={displayData?.platforms || ['meta']}
          onPublishComplete={handlePublishComplete}
        />
      )}

      {/* Organic Post Publishing Modal */}
      {conversationId && (
        <OrganicPostModal
          isOpen={showOrganicPostModal}
          onClose={() => setShowOrganicPostModal(false)}
          conversationId={conversationId}
          campaignTitle={displayData?.title || 'Campaign'}
          campaignConfig={campaignConfigWithAdjustments}
          onPublishComplete={handleOrganicPostComplete}
          conversationMedia={conversationMedia}
          resolvedMedia={resolvedMedia}
        />
      )}

      {/* Schedule Publish Modal */}
      {conversationId && (
        <SchedulePublishModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          conversationId={conversationId}
          campaignTitle={displayData?.title || 'Campaign'}
          campaignConfig={campaignConfigWithAdjustments}
          platforms={displayData?.platforms || ['meta']}
          publishType={schedulePublishType}
          onScheduleComplete={handleScheduleComplete}
          conversationMedia={conversationMedia}
          resolvedMedia={resolvedMedia}
        />
      )}

      {/* CSV Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        campaignData={campaignDataForExport}
        preselectedPlatforms={
          (displayData?.platforms || []).filter(
            (p: string): p is Platform => ['meta', 'google_ads', 'twitter', 'pinterest'].includes(p)
          )
        }
      />

      {/* Unified Image Editor (Position + Text) */}
      {cropModalTarget && (() => {
        const dims = getPlatformOutputDimensions(cropModalTarget.platform as any);
        const overlayHint = overlayHintMap.get(cropModalTarget.filename.toLowerCase()) ?? null;
        return (
          <UnifiedImageEditor
            editorContext="campaign"
            sourceImageUrl={cropModalTarget.originalUrl}
            platform={cropModalTarget.platform}
            outputWidth={dims.width}
            outputHeight={dims.height}
            existingAdjustment={
              mediaAdjustments[cropModalTarget.filename]?.[cropModalTarget.platform] ?? null
            }
            overlayHint={overlayHint}
            onApply={handleAdjustApply}
            onClose={() => setCropModalTarget(null)}
          />
        );
      })()}
    </div>
  );
};

export default CampaignProposalCard;
